import { HeadersOptionsType } from "@/types/ModuleOptionsType";
import { useSublinesVisibility } from "../../../hooks/useSublinesOverlinesVisibility";
import { bulmaColumnsToWidth, columnSize } from "@/utils/layout/columnSize";
import Split from "@/components/_primitives/Split/Split";
import HeaderBase from "@/components/_headers/HeaderBase/HeaderBase";
import { ImageType } from "@/types/baseTypes";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ParentsType } from "@/wp/getParentPages";
import { Post } from "@/wp/types/client";
import { Content } from "@/components/Content/Content";
import { getCategories } from "@/wp/getCategory";
import { useState, useEffect } from "react";
import { getVideoData } from "@/getData/getVideoData";
import { useRouter } from "next/router";
import { HeaderVideoPlayer } from "../HeaderVideoPlayer/HeaderVideoPlayer";
import { MediaItem } from "@/components/MediaItem/MediaItem";
import { Headline } from "@/components/Headline/Headline";

export interface MagazineHeaderProps {
  post: Post<"magazine">;
  headerOptions?: HeadersOptionsType["header_magazine"];
  fallbackImg?: ImageType | false;
  parents: ParentsType;
}
export const MagazineHeader = ({
  post,
  headerOptions,
  fallbackImg,
  parents,
}: MagazineHeaderProps) => {
  const header = post.acf.header_magazine;
  const {
    overline = "",
    subline = "",
    media,
    type,
    background_color_class,
    section_margin_class,
    row_direction_class,
    content,
    link,
    link_extended,
  } = header;

  const {
    show_overline_subline_headers: show_overline_subline,
    textheader,
    imageheader,
    splitheader,
    show_extended_link,
    breadcrumb_position,
  } = headerOptions ?? {};

  const { text_width: textheaderTextWidth } = textheader ?? {};

  // const { height: textheaderHeight } = textheader ?? {};
  const {
    // height: imageheaderHeight,
    // media_format: imageheaderMediaFormat,
    text_horizontal_align: imageheaderTextHorizontalAlign,
    text_vertical_align: imageheaderTextVerticalAlign,
    vertical_align: imageheaderVerticalAlign,
    headline_gradient: imageheaderHeadlineGradient,
    text_width: imageheaderTextWidth,
    image_height: imageheaderImageHeight,
  } = imageheader ?? {};

  const {
    image_height: splitheaderImageHeight,
    media_format: splitheaderMediaFormat,
    header_width: splitheaderWidth,
    text_vertical_align: splitheaderTextVerticalAlign,
    text_width_with_spacer: splitheaderTextWidthWithSpacer,
  } = splitheader ?? {};

  /* jscpd:ignore-start */
  const {
    showOverline,
    showSubline,
    showDate,
    showCategories,
    showDateAndCategories,
  } = useSublinesVisibility(show_overline_subline ?? "none");
  /* jscpd:ignore-end */
  const date = new Date(post.date_gmt).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const [postCategories, setPostCategories] = useState<string[]>([]);
  useEffect(() => {
    async function fetchCat() {
      const categories = await getCategories({ category: "categories" });
      setPostCategories(
        categories
          .filter((cat) => post?.categories?.includes(cat.id))
          .map((cat) => cat.name),
      );
    }
    fetchCat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const overlineJSX = `${showDate || showDateAndCategories ? date : ""}${showDateAndCategories ? " | " : ""}${(showCategories || showDateAndCategories) && postCategories.length > 0 ? `${postCategories.join(" | ")}` : ""}`;

  const splitHeaderDirection =
    row_direction_class === "is-row-normal-desktop" ? "row-reverse" : "row";

  const [videoData, setVideoData] = useState<Post<"videos"> | null>(null);
  const router = useRouter();
  useEffect(() => {
    if (!media || media.type !== "video") return;
    const videoId = "video" in media ? media.video?.[0] : undefined;
    async function fetchVideoData() {
      const videoResponse = await getVideoData({
        videoId: videoId as string,
        fields: "acf.header_videos,link",
        locale: router.locale,
        defaultLocale: router.defaultLocale,
      });
      if (videoResponse) {
        setVideoData(videoResponse);
      }
    }
    fetchVideoData();

    //eslint-disable-next-line
  }, []);

  const mediaJSX =
    media && media.type === "image" ? (
      <MediaItem
        image_desktop={
          media.image_desktop
            ? media.image_desktop
            : fallbackImg
              ? fallbackImg
              : false
        }
        image_tablet={media.image_tablet}
        image_mobile={media.image_mobile}
        image_fullhd={media.image_fullhd}
        // mediaFormat={imageheaderMediaFormat}
        sizes="(max-width: 767px) 50vw, 100vw"
        priority={true}
        mediaFormat={
          header.type === "splitheader" ? splitheaderMediaFormat : undefined
        }
        className={`${header.type === "splitheader" && splitheaderImageHeight === "image-height" ? "auto-height" : ""}`}
      />
    ) : media &&
      media.type === "video" &&
      videoData &&
      videoData.acf.header_videos.video_type === "upload" ? (
      <HeaderVideoPlayer
        video={videoData.acf.header_videos}
        autoplay={media.autoplay}
        singlePageUrl={videoData.link}
      />
    ) : (
      <></>
    );

  const contentJSX = (
    <>
      <Headline
        headline={header.title}
        headline_html_tag="h1-posts"
        extraClass={"is-flex is-flex-direction-column"}
        overline={
          showOverline
            ? overline
            : showDate || showCategories || showDateAndCategories
              ? overlineJSX
              : ""
        }
        subline={showSubline ? subline : ""}
        disableAnimation
      />
      <Content
        headline=""
        headline_html_tag="h2"
        headline_text_align_class={"has-text-left"}
        text_align_class={"has-text-left"}
        button_type="primary"
        text={content}
        link={!show_extended_link && link}
        link_extended={show_extended_link && link_extended}
        isHeader={true}
      />
    </>
  );

  const breadcrumbsJSX = (
    <div className="nav-oriented-breadcrumbs">
      <Breadcrumbs
        parents={parents}
        post={post}
        breadcrumbPositon={
          breadcrumb_position
            ? breadcrumb_position
            : "is-justify-content-flex-start"
        }
      />
    </div>
  );

  switch (type) {
    case "textheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          header={breadcrumbsJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(textheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment="center"
          height={"content-height"}
        />
      );
    case "imageheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          header={mediaJSX}
          content={contentJSX}
          headerFrame={"frameless"}
          contentFrame={"narrow-frame"}
          narrowFrameColumns={
            columnSize(imageheaderTextWidth ?? false) || "is-12"
          }
          contentHorizontalAlignment={imageheaderTextHorizontalAlign || "start"}
          contentVerticalAlignment={imageheaderTextVerticalAlign || "start"}
          height={
            imageheaderImageHeight === "screen-height"
              ? "screen-height"
              : "content-height"
          }
          heightDependency="content"
          arrangement={
            imageheaderVerticalAlign === "overlay"
              ? "overlay"
              : imageheaderVerticalAlign === "media-top-text-bottom"
                ? "content-bottom"
                : "content-top"
          }
          gradient={imageheaderHeadlineGradient ?? false}
        />
      );
    case "splitheader":
      return (
        <HeaderBase
          className={`${background_color_class || ""} ${section_margin_class}`}
          contentFrame={
            splitheaderImageHeight === "screen-height-frameless"
              ? "frameless"
              : "narrow-frame"
          }
          headerFrame={"frameless"}
          contentHorizontalAlignment="center"
          height={
            splitheaderImageHeight === "screen-height-frameless"
              ? "screen-height"
              : "content-height"
          }
          narrowFrameColumns={
            splitheaderImageHeight === "screen-height-frameless"
              ? "is-12"
              : columnSize(splitheaderWidth ?? false) || "is-12"
          }
          arrangement="content-bottom"
          header={
            splitheaderImageHeight === "screen-height-frameless"
              ? undefined
              : breadcrumbsJSX
          }
          content={
            <Split
              first={mediaJSX}
              firstClassName={"min-height-acceptor-desktop"}
              second={contentJSX}
              direction={splitHeaderDirection}
              firstVerticalAlignment={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "stretch"
                  : "start"
              }
              secondVerticalAlignment={splitheaderTextVerticalAlign}
              secondOuterFractional={
                splitheaderImageHeight === "screen-height-frameless"
                  ? breadcrumbsJSX
                  : undefined
              }
              secondClassName="section-padding-y"
              verticalBreapoints={"tablet"}
              sticky="first"
              stickyTop={
                splitheaderImageHeight === "screen-height-frameless"
                  ? "var(--navbar-height)"
                  : "calc(var(--navbar-height) + var(--section-padding-y))"
              }
              stickyMaxHeight="calc(100dvh - var(--navbar-height))"
              gap={"8.3333%"}
              gapOn="second"
              firstWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: `calc(100% - ${bulmaColumnsToWidth(curr.number_of_cols)})`,
                      };
                    }, {})
              }
              secondWidth={
                splitheaderImageHeight === "screen-height-frameless" ||
                !splitheaderTextWidthWithSpacer
                  ? "50%"
                  : splitheaderTextWidthWithSpacer.reduce((prev, curr) => {
                      if (!curr.breakpoint) {
                        return prev;
                      }
                      return {
                        ...prev,
                        [curr.breakpoint]: bulmaColumnsToWidth(
                          curr.number_of_cols,
                        ),
                      };
                    }, {})
              }
              containerWidthLimit={{
                first: false,
                second: splitheaderImageHeight === "screen-height-frameless",
              }}
            />
          }
        />
      );
  }
};
